import { purchaseCost } from '../config/catalog';
import { SLOTS } from '../config/map';
import { collectCueEligible } from '../production/collectCues';
import { entityAtSlot, isParcelUnlocked } from '../sim/park';
import { centerAnchorForSlotId } from '../sim/placement';
import { evaluatePlacementPreview } from '../sim/placementPreview';
import { hasBuiltKind } from '../sim/unlocks';
import type { EntityKind, ParkState, PlacedEntity, SubcellAnchor } from '../types';

/**
 * Guided first-park stages. Legacy `build` / `collect` / `upgrade` stay in the
 * union so the unused Phaser scene still typechecks; the resolver never returns them.
 */
export type OnboardingStep =
  | 'build_ride'
  | 'build_snack'
  | 'collect_drink'
  | 'build_drink'
  | 'complete'
  | 'build'
  | 'collect'
  | 'upgrade';

/** Tiny session-local flags. Not persisted and not required to resume the tutorial. */
export interface OnboardingFlags {
  guidedCollectDone: boolean;
  tutorialCollectTracked: boolean;
}

export function createOnboardingFlags(): OnboardingFlags {
  return {
    guidedCollectDone: false,
    tutorialCollectTracked: false,
  };
}

export type PrimaryGuide =
  | { target: 'build_button' }
  | { target: 'catalog'; kind: EntityKind }
  | { target: 'placement'; kind: EntityKind }
  | { target: 'collect'; entityId: string }
  | { target: 'none' };

const TUTORIAL_BUILD_KINDS = ['starter_ride', 'snack_kiosk', 'drink_kiosk'] as const;

export function drinkKioskGuideCost(): number {
  return purchaseCost('drink_kiosk');
}

export function isBuildDirectedStep(step: OnboardingStep): boolean {
  return step === 'build_ride' || step === 'build_snack' || step === 'build_drink';
}

export function guidedCatalogKind(step: OnboardingStep): EntityKind | null {
  switch (step) {
    case 'build_ride':
      return 'starter_ride';
    case 'build_snack':
      return 'snack_kiosk';
    case 'build_drink':
      return 'drink_kiosk';
    default:
      return null;
  }
}

export function tutorialBuildLessonsComplete(state: ParkState): boolean {
  return TUTORIAL_BUILD_KINDS.every((kind) => hasBuiltKind(state, kind));
}

/**
 * Progress comes from persisted `builtKindMilestones` (or a live entity, for
 * older saves). Session flags are ignored so a reload resumes the remaining step.
 */
export function resolveOnboardingStep(
  state: ParkState,
  _flags: Pick<OnboardingFlags, 'guidedCollectDone'>,
): OnboardingStep {
  if (!hasBuiltKind(state, 'starter_ride')) {
    return 'build_ride';
  }
  if (!hasBuiltKind(state, 'snack_kiosk')) {
    return 'build_snack';
  }
  if (!hasBuiltKind(state, 'drink_kiosk')) {
    return state.coins < drinkKioskGuideCost() ? 'collect_drink' : 'build_drink';
  }
  return 'complete';
}

export function guidedCollectEntity(state: ParkState): PlacedEntity | undefined {
  return state.entities.find((entity) => collectCueEligible(entity));
}

export function recommendedGuideAnchor(state: ParkState, kind: EntityKind): SubcellAnchor | null {
  for (const slot of SLOTS) {
    if (!isParcelUnlocked(state, slot.parcelId) || entityAtSlot(state, slot.id)) {
      continue;
    }
    const anchor = centerAnchorForSlotId(slot.id);
    if (evaluatePlacementPreview(state, { mode: 'build', kind, anchor, rotation: 0 }).valid) {
      return anchor;
    }
  }
  return null;
}

export function recommendedGuideSlotId(state: ParkState, kind: EntityKind): string | null {
  const anchor = recommendedGuideAnchor(state, kind);
  if (!anchor) {
    return null;
  }
  for (const slot of SLOTS) {
    const center = centerAnchorForSlotId(slot.id);
    if (center.sx === anchor.sx && center.sz === anchor.sz) {
      return slot.id;
    }
  }
  return null;
}

export function firstUnlockedEmptySlotId(
  state: ParkState,
  preferredSlotId?: string | null,
): string | null {
  if (preferredSlotId) {
    const occupied = entityAtSlot(state, preferredSlotId);
    const slot = SLOTS.find((item) => item.id === preferredSlotId);
    if (slot && !occupied && isParcelUnlocked(state, slot.parcelId)) {
      return preferredSlotId;
    }
  }
  for (const slot of SLOTS) {
    if (isParcelUnlocked(state, slot.parcelId) && !entityAtSlot(state, slot.id)) {
      return slot.id;
    }
  }
  return null;
}

export function resolveOnboardingTarget(
  state: ParkState,
  flags: Pick<OnboardingFlags, 'guidedCollectDone'>,
  _preferredSlotId?: string | null,
): { step: OnboardingStep; slotId: string | null; entityId: string | null } {
  const step = resolveOnboardingStep(state, flags);
  const kind = guidedCatalogKind(step);
  if (kind) {
    return { step, slotId: recommendedGuideSlotId(state, kind), entityId: null };
  }
  if (step === 'collect_drink') {
    const entity = guidedCollectEntity(state);
    return { step, slotId: entity?.slotId ?? null, entityId: entity?.id ?? null };
  }
  return { step, slotId: null, entityId: null };
}

export function shouldShowOnboardingCue(
  state: ParkState,
  flags: Pick<OnboardingFlags, 'guidedCollectDone'>,
  preferredSlotId?: string | null,
): boolean {
  const target = resolveOnboardingTarget(state, flags, preferredSlotId);
  if (isBuildDirectedStep(target.step)) {
    return target.slotId !== null;
  }
  if (target.step === 'collect_drink') {
    return target.entityId !== null;
  }
  return false;
}

/** At most one primary cue. Placement wins over the catalog, which wins over the Build button. */
export function primaryGuideTarget(input: {
  state: ParkState;
  flags: Pick<OnboardingFlags, 'guidedCollectDone'>;
  catalogOpen: boolean;
  placingKind: EntityKind | null;
}): PrimaryGuide {
  const step = resolveOnboardingStep(input.state, input.flags);
  const kind = guidedCatalogKind(step);
  if (kind && input.placingKind === kind && recommendedGuideAnchor(input.state, kind)) {
    return { target: 'placement', kind };
  }
  if (kind && input.catalogOpen) {
    return { target: 'catalog', kind };
  }
  if (kind && shouldShowOnboardingCue(input.state, input.flags)) {
    return { target: 'build_button' };
  }
  if (step === 'collect_drink') {
    const entity = guidedCollectEntity(input.state);
    if (entity) {
      return { target: 'collect', entityId: entity.id };
    }
  }
  return { target: 'none' };
}
